import { create } from 'zustand';
import { useListStore } from '../list';
import { useUsersStore } from '../users';
import { IReportsStore } from './types';
import { reportsApi } from '@/API';
import { IReportFilterParams } from '@/types';
import {
  getTimeZoneOffsetInMinutes,
  convertToUTC,
  processDatesInParams,
} from '@/utils/convertDate';

export const useReportsStore = create<IReportsStore>((set) => ({
  reportConfig: [],
  reportData: [],
  loading: true,
  isChartDataLoading: false,

  getReportConfig: async () => {
    try {
      set({ loading: true });
      const config = await reportsApi.getReportConfig();
      const processedConfig = config
        .filter((item) => item)
        .map((item) => ({
          ...item,
          charts: item.charts.filter((chart) =>
            [
              'bar',
              'stacked_bar',
              'line',
              'horizontal_bar',
              'single',
              'table',
              'label',
            ].includes(chart.type),
          ),
          filters: item.filters?.map((filter) => ({
            ...filter,
            title: filter.name || '',
            allOptionId: filter.options?.some((option) => option.key === '_all')
              ? '_all'
              : undefined,
            options: filter.options?.map((option) => ({
              ...option,
              id: option.key,
              title: option.value || '',
            })),
          })),
        }));
      set({ reportConfig: processedConfig });
    } catch (error) {
      console.error('Failed to load report config:', error);
      set({ reportConfig: [] });
    } finally {
      set({ loading: false });
    }
  },

  fetchReportData: async (
    reportId: string,
    filterParams: IReportFilterParams = {},
  ) => {
    set({ reportData: [], isChartDataLoading: true });
    try {
      const userData = useUsersStore.getState().userData;
      const timezones = useListStore.getState().timezones;
      const convertFnUTC = (date: string) =>
        convertToUTC(
          date,
          getTimeZoneOffsetInMinutes(userData?.timezone, timezones, date),
        );
      const modifiedParams = processDatesInParams(filterParams, convertFnUTC);
      const rawData = await reportsApi.getReportDataById(
        reportId,
        modifiedParams,
      );
      set({ reportData: rawData });
    } catch (error) {
      console.error('Failed to load chart data:', error);
      set({ reportData: [] });
    } finally {
      set({ isChartDataLoading: false });
    }
  },

  resetReportData: () => {
    set({ reportData: [] });
  },
}));
